import { and, eq } from "drizzle-orm";
import {
  type PowerSyncDrizzleDB,
  purchase_order,
  purchase_order_line,
  supplier,
} from "@comandero/powersync";
import { livePurchaseOrderLineFilters } from "../live/filters";
import { nowISO } from "../utils/date-helpers";
import { generateUUID } from "../utils/uuid";
import { purchaseLineTotal, purchaseOrderTotal } from "./calc";

export type PurchaseOrderStatus = "draft" | "confirmed" | "received";

/** Crea un proveedor. CC-7: escritura con await. */
export async function createSupplierMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  name: string,
  opts?: { contact?: string | null; phone?: string | null },
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(supplier).values({
    id,
    organization_id: organizationId,
    name: name.trim(),
    contact: opts?.contact ?? null,
    phone: opts?.phone ?? null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function deleteSupplierMutation(
  db: PowerSyncDrizzleDB,
  supplierId: string,
): Promise<void> {
  const now = nowISO();
  await db
    .update(supplier)
    .set({ deleted_at: now, updated_at: now })
    .where(eq(supplier.id, supplierId));
}

/** Crea una orden de compra en borrador. */
export async function createPurchaseOrderMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  opts?: { branchId?: string | null; supplierId?: string | null; note?: string | null },
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(purchase_order).values({
    id,
    organization_id: organizationId,
    branch_id: opts?.branchId ?? null,
    supplier_id: opts?.supplierId ?? null,
    status: "draft",
    total: 0,
    note: opts?.note ?? null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/**
 * Agrega una línea a una OC (ingrediente, cantidad y costo unitario) y recalcula
 * el total de la OC. Cálculo de totales PURO (calc.ts). CC-7: escrituras con await.
 */
export async function addPurchaseLineMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  purchaseOrderId: string,
  ingredientId: string,
  qty: number,
  unitCost: number,
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  const safeQty = Math.max(0, Math.trunc(qty));
  const safeCost = Math.max(0, Math.trunc(unitCost));
  const total = purchaseLineTotal({ qty: safeQty, unitCost: safeCost });
  await db.insert(purchase_order_line).values({
    id,
    organization_id: organizationId,
    purchase_order_id: purchaseOrderId,
    ingredient_id: ingredientId,
    qty: safeQty,
    unit_cost: safeCost,
    line_total: total,
    created_at: now,
    updated_at: now,
  });
  await recomputePurchaseOrderTotal(db, purchaseOrderId);
  return id;
}

/** Recalcula el total de una OC a partir de sus líneas (cálculo puro). */
export async function recomputePurchaseOrderTotal(
  db: PowerSyncDrizzleDB,
  purchaseOrderId: string,
): Promise<void> {
  const lines = await db
    .select()
    .from(purchase_order_line)
    .where(
      and(
        eq(purchase_order_line.purchase_order_id, purchaseOrderId),
        ...livePurchaseOrderLineFilters(),
      ),
    );
  const total = purchaseOrderTotal(
    lines.map((l) => ({ qty: l.qty ?? 0, unitCost: l.unit_cost ?? 0 })),
  );
  await db
    .update(purchase_order)
    .set({ total, updated_at: nowISO() })
    .where(eq(purchase_order.id, purchaseOrderId));
}

/**
 * Cambia el estado de una OC. Al pasar a `received`, el write-path server-side
 * sube el stock de los ingredientes de forma IDEMPOTENTE (no lo escribe el
 * cliente; ver purchase_order_with_stock en core-rust). CC-7: escritura con await.
 */
export async function setPurchaseOrderStatusMutation(
  db: PowerSyncDrizzleDB,
  purchaseOrderId: string,
  status: PurchaseOrderStatus,
): Promise<void> {
  await db
    .update(purchase_order)
    .set({ status, updated_at: nowISO() })
    .where(eq(purchase_order.id, purchaseOrderId));
}

/** Recibe una OC: marca `received` para que el server suba el stock. */
export async function receivePurchaseOrderMutation(
  db: PowerSyncDrizzleDB,
  purchaseOrderId: string,
): Promise<void> {
  await setPurchaseOrderStatusMutation(db, purchaseOrderId, "received");
}
